import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export default function Pagination({ 
  currentPage = 1, 
  totalPages = 1, 
  totalRecords = 0,
  recordsPerPage = 10,
  onPageChange,
  showInfo = true,
  size = 'md', // 'sm', 'md', 'lg'
  color = 'green', // 'green', 'blue', 'purple'
  showRowsPerPage = false,
  rowsPerPageOptions = [10, 20, 50, 100],
  onRowsPerPageChange,
  rowsPerPageLabel = 'Rows per page'
}) {
  if (!totalPages || totalPages <= 1) {
    return null; // Don't show pagination if there's only one page or no data
  }

  const startRecord = (currentPage - 1) * recordsPerPage + 1;
  const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

  // Color schemes
  const colors = {
    green: {
      text: 'text-green-600',
      hoverText: 'hover:text-green-800',
      hoverBg: 'hover:bg-green-100',
      activeBg: 'bg-green-600',
      activeText: 'text-white',
      infoBg: 'bg-green-50',
      infoText: 'text-green-600'
    },
    blue: {
      text: 'text-blue-600',
      hoverText: 'hover:text-blue-800',
      hoverBg: 'hover:bg-blue-100',
      activeBg: 'bg-blue-600',
      activeText: 'text-white',
      infoBg: 'bg-blue-50',
      infoText: 'text-blue-600'
    },
    purple: {
      text: 'text-purple-600',
      hoverText: 'hover:text-purple-800',
      hoverBg: 'hover:bg-purple-100',
      activeBg: 'bg-purple-600',
      activeText: 'text-white',
      infoBg: 'bg-purple-50',
      infoText: 'text-purple-600'
    }
  };

  // Size variants
  const sizes = {
    sm: {
      container: 'gap-1',
      button: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3',
      info: 'text-xs'
    },
    md: {
      container: 'gap-2',
      button: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4',
      info: 'text-sm'
    },
    lg: {
      container: 'gap-3',
      button: 'px-4 py-2 text-base',
      icon: 'w-5 h-5',
      info: 'text-base'
    }
  };

  const currentColor = colors[color] || colors.green;
  const currentSize = sizes[size] || sizes.md;

  const getVisiblePages = () => {
    const maxVisible = 5;
    const pages = [];
    
    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination
      if (currentPage <= 3) {
        // Show first pages
        for (let i = 1; i <= Math.min(4, totalPages); i++) {
          pages.push(i);
        }
        if (totalPages > 4) {
          pages.push('...');
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 2) {
        // Show last pages
        pages.push(1);
        if (totalPages > 4) {
          pages.push('...');
        }
        for (let i = Math.max(totalPages - 3, 2); i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show middle pages
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2">
      {/* Rows per page selector */}
      {showRowsPerPage && (
        <div className="flex items-center gap-2">
          <span className={`${currentSize.info} text-gray-700 font-medium`}>
            {rowsPerPageLabel}:
          </span>
          <select
            value={recordsPerPage}
            onChange={(e) => onRowsPerPageChange?.(Number(e.target.value))}
            className={`border border-gray-300 rounded-md px-2 py-1 ${currentSize.info} text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            {rowsPerPageOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Info section */}
      {showInfo && (
        <div className={`${currentSize.info} ${currentColor.infoText} font-medium ${showRowsPerPage ? 'hidden sm:block' : ''}`}>
          Showing {startRecord} to {endRecord} of {totalRecords} results
        </div>
      )}

      {/* Pagination controls */}
      <div className={`flex items-center ${currentSize.container}`}>
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`flex items-center gap-1 ${currentSize.button} rounded font-medium transition-colors ${
            currentPage === 1
              ? 'text-gray-400 cursor-not-allowed'
              : `${currentColor.text} ${currentColor.hoverText} ${currentColor.hoverBg}`
          }`}
        >
          <ChevronLeftIcon className={currentSize.icon} />
          Previous
        </button>

        {/* Page numbers */}
        <div className={`flex items-center ${currentSize.container}`}>
          {visiblePages.map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' ? onPageChange(page) : null}
              disabled={page === '...'}
              className={`${currentSize.button} rounded font-medium transition-colors ${
                page === currentPage
                  ? `${currentColor.activeBg} ${currentColor.activeText}`
                  : page === '...'
                  ? 'text-gray-400 cursor-default'
                  : `${currentColor.text} ${currentColor.hoverText} ${currentColor.hoverBg}`
              }`}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`flex items-center gap-1 ${currentSize.button} rounded font-medium transition-colors ${
            currentPage === totalPages
              ? 'text-gray-400 cursor-not-allowed'
              : `${currentColor.text} ${currentColor.hoverText} ${currentColor.hoverBg}`
          }`}
        >
          Next
          <ChevronRightIcon className={currentSize.icon} />
        </button>
      </div>
    </div>
  );
}
